// src/Component/chat/component/ChatContent.tsx
import { useState, useContext, useRef, useEffect, useLayoutEffect } from "react";
import { Icon } from "@iconify/react";
import { Badge, Button } from "flowbite-react";
import SimpleBar from "simplebar-react";
import ChatInsideSidebar from "./ChatInsideSidebar";
import ChatMsgSent from "./ChatMsgSent";
import MediaViewer from "./MediaViewer";
import { 
  formatDistanceToNowStrict, 
  isToday, 
  isYesterday, 
  isThisWeek,
  format 
} from "date-fns";
import { ChatContext } from "../../../context/ChatContext/chatContext";
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { FIREBASE_COLLECTIONS } from '../../api/url';
import React from 'react';
import Linkify from 'linkify-react';

type Props = {
  onClickMobile: (event: React.MouseEvent<HTMLElement>) => void;
};

interface SenderInfo {
  name: string;
  avatar: string | null;
}

const ChatContent = ({ onClickMobile }: Props) => {
  const { selectedChat, currentUserId, loading, messagesLoading, markMessagesAsRead } = useContext(ChatContext);
  const scrollableNodeRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousChatIdRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef<{[key: string]: boolean}>({});
  
  const markedAsReadRef = useRef<{[key: string]: boolean}>({});
  const [isChatFullyVisible, setIsChatFullyVisible] = useState(false);

  const [newMessageCount, setNewMessageCount] = useState(0);
  const [showNewMessageBadge, setShowNewMessageBadge] = useState(false);
  const previousMessageCountRef = useRef<number>(0);
  const newMessageTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isRightSide, setIsRightSide] = useState(false);
  const handleButtonClick = () => setIsRightSide(!isRightSide);

  const [isOpenMedia, setIsOpenMedia] = useState(false);
  const handleClose = () => setIsOpenMedia(false);

  const [imageErrors, setImageErrors] = useState<{[key: string]: boolean}>({});

  // ✅ Use a ref to track which IDs have already been fetched — avoids stale closure
  const fetchedSenderIdsRef = useRef<Set<string>>(new Set());
  const [senderInfoMap, setSenderInfoMap] = useState<{[senderId: string]: SenderInfo}>({});

  const [chatUserStatus, setChatUserStatus] = useState<string>('offline');
  const [lastSeenTime, setLastSeenTime] = useState<Date | null>(null);
  const [chatUserId, setChatUserId] = useState<string | null>(null);

  const [mediaViewer, setMediaViewer] = useState<{
    isOpen: boolean;
    url: string;
    type: 'image' | 'file';
    fileName?: string;
  }>({
    isOpen: false,
    url: '',
    type: 'image',
  });

  // Get the client user ID from the chat group members
  useEffect(() => {
    const getChatUserId = async () => {
      if (!selectedChat?.id) { setChatUserId(null); return; }
      try {
        const chatGroupRef = doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, selectedChat.id as string);
        const chatGroupDoc = await getDoc(chatGroupRef);
        if (chatGroupDoc.exists()) {
          const members: string[] = chatGroupDoc.data()?.members || [];
          const clientUserId = members.find((id: string) => id.startsWith('userid_'));
          setChatUserId(clientUserId || null);
        }
      } catch (error) {
        console.error('❌ Error getting chat user ID:', error);
        setChatUserId(null);
      }
    };
    getChatUserId();
  }, [selectedChat?.id, currentUserId]);

  // Subscribe to client's online status
  useEffect(() => {
    if (!chatUserId) { setChatUserStatus('offline'); setLastSeenTime(null); return; }
    const userDocRef = doc(db, FIREBASE_COLLECTIONS.USERS, chatUserId);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setChatUserStatus(data?.onlineStatus || 'offline');
        if (data?.lastSeen?.toDate) setLastSeenTime(data.lastSeen.toDate());
      } else {
        setChatUserStatus('offline');
      }
    }, () => setChatUserStatus('offline'));
    return () => unsubscribe();
  }, [chatUserId]);

  // Reset state when chat changes
  useEffect(() => {
    const currentChatId = selectedChat?.id as string;
    if (currentChatId && currentChatId !== previousChatIdRef.current) {
      setIsChatFullyVisible(false);
      markedAsReadRef.current[currentChatId] = false;
      setNewMessageCount(0);
      setShowNewMessageBadge(false);
      previousMessageCountRef.current = 0;
      // ✅ Reset both the ref and state when switching chats
      fetchedSenderIdsRef.current = new Set();
      setSenderInfoMap({});
      if (newMessageTimerRef.current) { clearTimeout(newMessageTimerRef.current); newMessageTimerRef.current = null; }
    }
  }, [selectedChat?.id]);

  // ✅ Fetch sender info using a ref guard — no stale closure issues
  const fetchSenderInfo = async (senderId: string) => {
    // Guard: skip if already fetched or currently the logged-in user
    if (!senderId || senderId === currentUserId) return;
    if (fetchedSenderIdsRef.current.has(senderId)) return;

    // Mark as fetched immediately to prevent duplicate requests
    fetchedSenderIdsRef.current.add(senderId);

    try {
      console.log('🔍 Fetching sender info for:', senderId);
      const userDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.USERS, senderId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const name   = `${data.user_fname || ''} ${data.user_lname || ''}`.trim() || senderId;
        const avatar = data.profile_url && data.profile_url.trim() !== '' ? data.profile_url : null;
        console.log('✅ Sender info loaded:', senderId, '→', name, '| avatar:', avatar);
        setSenderInfoMap(prev => ({ ...prev, [senderId]: { name, avatar } }));
      } else {
        console.warn('⚠️ No Firestore doc found for sender:', senderId);
        // Still store a fallback so we don't retry endlessly
        setSenderInfoMap(prev => ({ ...prev, [senderId]: { name: senderId, avatar: null } }));
      }
    } catch (e) {
      console.error('❌ Failed to fetch sender info for:', senderId, e);
      // Store fallback on error too
      fetchedSenderIdsRef.current.delete(senderId); // allow retry next time
    }
  };

  // Trigger fetches whenever messages list changes
  useEffect(() => {
    const msgs = selectedChat?.messages && Array.isArray(selectedChat.messages)
      ? selectedChat.messages
      : [];
    if (!msgs.length) return;

    msgs.forEach((m: any) => {
      if (m.senderId && m.senderId !== currentUserId) {
        fetchSenderInfo(m.senderId);
      }
    });
  }, [selectedChat?.messages?.length, selectedChat?.id, currentUserId]);

  // Detect new incoming messages while chat is open
  useEffect(() => {
    const currentChatId = selectedChat?.id as string;
    const messages      = selectedChat?.messages || [];
    const messageCount  = messages.length;
    if (!currentChatId || !isChatFullyVisible) return;

    if (previousMessageCountRef.current > 0 && messageCount > previousMessageCountRef.current) {
      const newCount   = messageCount - previousMessageCountRef.current;
      const newMessages = messages.slice(-newCount);
      const fromOthers  = newMessages.filter((msg) => msg.senderId !== currentUserId);
      if (fromOthers.length > 0) {
        setNewMessageCount(fromOthers.length);
        setShowNewMessageBadge(true);
        if (newMessageTimerRef.current) clearTimeout(newMessageTimerRef.current);
        newMessageTimerRef.current = setTimeout(() => {
          setShowNewMessageBadge(false);
          setNewMessageCount(0);
          if (currentChatId && currentUserId) {
            markMessagesAsRead(currentChatId);
            markedAsReadRef.current[currentChatId] = true;
          }
        }, 3000);
      }
    }
    previousMessageCountRef.current = messageCount;
  }, [selectedChat?.messages?.length, isChatFullyVisible, currentUserId, selectedChat?.id]);

  // Mark as read when chat is fully visible and loaded
  useEffect(() => {
    const currentChatId = selectedChat?.id as string;
    if (!currentChatId || !currentUserId) return;
    const isLoading  = messagesLoading[currentChatId];
    const hasMessages = selectedChat?.messages && selectedChat.messages.length > 0;
    if (!isLoading && hasMessages && isChatFullyVisible && !markedAsReadRef.current[currentChatId] && !showNewMessageBadge) {
      const timer = setTimeout(() => {
        markMessagesAsRead(currentChatId);
        markedAsReadRef.current[currentChatId] = true;
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [selectedChat?.id, currentUserId, messagesLoading, isChatFullyVisible, selectedChat?.messages?.length, showNewMessageBadge]);

  const getUserInitials = (name: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
    return parts[0].charAt(0).toUpperCase();
  };

  const handleMediaClick = (url: string, type: 'image' | 'file', fileName?: string) => {
    setMediaViewer({ isOpen: true, url, type, fileName });
  };

  const formatWhatsAppTime = (date: Date | null): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isToday(d)) return format(d, 'HH:mm');
    if (isYesterday(d)) return 'Yesterday';
    if (isThisWeek(d, { weekStartsOn: 1 })) return format(d, 'EEEE');
    return format(d, 'dd/MM/yyyy');
  };

  const getStatusText = () => {
    if (chatUserStatus === 'online') return <span className="text-green-600 font-medium">Online</span>;
    if (chatUserStatus === 'away')   return <span className="text-yellow-600">Away</span>;
    if (chatUserStatus === 'busy')   return <span className="text-red-600">Busy</span>;
    if (lastSeenTime) return <span className="text-gray-500">Last seen {formatDistanceToNowStrict(lastSeenTime, { addSuffix: true })}</span>;
    return <span className="text-gray-500 capitalize">{chatUserStatus}</span>;
  };

  const getStatusBadge = (status: string) => {
    let bg = 'bg-gray-200';
    if (status === 'online') bg = 'bg-green-500';
    else if (status === 'busy') bg = 'bg-red-500';
    else if (status === 'away') bg = 'bg-yellow-500';
    return <span className={`absolute bottom-0 right-0 h-2 w-2 ${bg} rounded-full ring-2 ring-white`} style={{ bottom: '2px', right: '2px' }} />;
  };

  const getSenderName   = (senderId: string): string =>
    senderInfoMap[senderId]?.name || selectedChat?.name || 'Unknown';

  const getSenderAvatar = (senderId: string): string | null =>
    senderInfoMap[senderId]?.avatar || null;

  const isLoadingMessages = selectedChat ? messagesLoading[selectedChat.id as string] : false;
  const messages   = selectedChat?.messages && Array.isArray(selectedChat.messages) ? selectedChat.messages : [];
  const hasMessages = messages.length > 0;

  const scrollToBottom = (instant: boolean = false) => {
    if (scrollableNodeRef.current) {
      const el = scrollableNodeRef.current.getScrollElement();
      if (el) {
        el.style.scrollBehavior = instant ? 'auto' : 'smooth';
        el.scrollTop = el.scrollHeight;
        if (instant) setTimeout(() => { el.style.scrollBehavior = 'smooth'; }, 50);
      }
    }
  };

  useLayoutEffect(() => {
    const currentChatId = selectedChat?.id as string;
    if (currentChatId && currentChatId !== previousChatIdRef.current) {
      isInitialLoadRef.current[currentChatId] = true;
      if (scrollableNodeRef.current) {
        const el = scrollableNodeRef.current.getScrollElement();
        if (el) { el.style.scrollBehavior = 'auto'; el.scrollTop = 999999; }
      }
      previousChatIdRef.current = currentChatId;
    }
  }, [selectedChat?.id]);

  useLayoutEffect(() => {
    const currentChatId = selectedChat?.id as string;
    if (currentChatId && isInitialLoadRef.current[currentChatId]) {
      if (scrollableNodeRef.current) {
        const el = scrollableNodeRef.current.getScrollElement();
        if (el) { el.style.scrollBehavior = 'auto'; el.scrollTop = el.scrollHeight; }
      }
      if (hasMessages && !isLoadingMessages) {
        setTimeout(() => {
          if (scrollableNodeRef.current) {
            const el = scrollableNodeRef.current.getScrollElement();
            if (el) { el.scrollTop = el.scrollHeight; el.style.scrollBehavior = 'smooth'; }
          }
          isInitialLoadRef.current[currentChatId] = false;
          setIsChatFullyVisible(true);
          previousMessageCountRef.current = messages.length;
        }, 50);
      }
    }
  }, [selectedChat?.id, hasMessages, isLoadingMessages, messages.length]);

  useEffect(() => {
    const currentChatId = selectedChat?.id as string;
    if (currentChatId && !isInitialLoadRef.current[currentChatId] && hasMessages && !isLoadingMessages) {
      scrollToBottom(false);
    }
  }, [messages.length]);

  useEffect(() => { setImageErrors({}); }, [selectedChat?.id]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mediaViewer.isOpen) setMediaViewer({ ...mediaViewer, isOpen: false });
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mediaViewer]);

  useEffect(() => {
    return () => { if (newMessageTimerRef.current) clearTimeout(newMessageTimerRef.current); };
  }, []);

  if (!selectedChat) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center px-6">
          <div className="mb-6">
            <Icon icon="solar:chat-round-dots-bold-duotone" height={120} className="mx-auto text-gray-300" />
          </div>
          <h3 className="text-2xl font-semibold mb-3 text-gray-700">Welcome to Chat</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
            Select a conversation from the list to start messaging with your contacts
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-5 border-b border-ld bg-white flex-shrink-0">
        <div className="flex items-center justify-between lg:flex-nowrap flex-wrap lg:gap-0 gap-2">
          <div className="flex items-center gap-3">
            <Button color={"lightprimary"} className="btn-circle p-0 lg:hidden flex" onClick={onClickMobile}>
              <Icon icon="solar:hamburger-menu-outline" height={18} />
            </Button>
            <div className="relative sm:min-w-12 min-w-9 flex-shrink-0">
              {selectedChat.thumb && selectedChat.thumb.trim() !== "" && !imageErrors['header'] ? (
                <div className="relative">
                  <img
                    src={selectedChat.thumb}
                    height={48} width={48}
                    alt={selectedChat.name}
                    className="rounded-full sm:h-12 sm:w-12 h-9 w-9 object-cover"
                    onError={() => setImageErrors((prev) => ({ ...prev, header: true }))}
                  />
                  {getStatusBadge(chatUserStatus)}
                </div>
              ) : (
                <div className="relative">
                  <div className="rounded-full sm:h-12 sm:w-12 h-9 w-9 bg-[#DAF9F9] flex items-center justify-center flex-shrink-0">
                    <span className="sm:text-lg text-base font-semibold text-[#00A9A9]">
                      {getUserInitials(selectedChat.name)}
                    </span>
                  </div>
                  {getStatusBadge(chatUserStatus)}
                </div>
              )}
            </div>
            <div>
              <h5 className="text-base sm:mb-1 font-semibold">{selectedChat.name}</h5>
              <div className="text-xs opacity-90">{getStatusText()}</div>
            </div>
          </div>
          <div className="flex items-center md:gap-2 gap-1">
            <div className="btn-circle-hover cursor-pointer sm:h-10 sm:w-10 h-9 w-9 xl:flex hidden items-center justify-center" onClick={handleButtonClick}>
              <Icon icon="heroicons-outline:dots-vertical" height={20} />
            </div>
            <div className="btn-circle-hover cursor-pointer sm:h-10 sm:w-10 h-9 w-9 xl:hidden flex items-center justify-center" onClick={() => setIsOpenMedia(true)}>
              <Icon icon="heroicons-outline:dots-vertical" height={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Messages + Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <div className={`${!isRightSide ? "w-full" : "xl:w-[calc(100%_-_260px)] w-full"} ${!isRightSide ? 'border-none' : 'xl:border-e'} border-ld h-full chat-messages-container`}>
          <SimpleBar style={{ maxHeight: '100%', height: '100%' }} autoHide={false} ref={scrollableNodeRef}>
            <div className="chat-messages-wrapper">
              <div className="px-5 py-5 messages-content">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full py-20">
                    <div className="text-center">
                      <Icon icon="solar:loading-outline" height={40} className="mx-auto mb-3 text-primary animate-spin" />
                      <p className="text-sm text-gray-500">Loading messages...</p>
                    </div>
                  </div>
                ) : hasMessages ? (
                  messages.map((msg: any, index: number) => {
                    const isReceived    = msg.senderId !== currentUserId;
                    const formattedTime = formatWhatsAppTime(msg.createdAt);
                    const senderName    = getSenderName(msg.senderId);
                    const senderAvatar  = getSenderAvatar(msg.senderId);
                    const avatarErrorKey = `avatar-${msg.senderId}`;

                    return (
                      <div className="mb-6" key={`${msg.id}-${index}`}>
                        {isReceived ? (
                          <div className="flex gap-3">
                            <div className="w-8 h-8 flex-shrink-0">
                              {senderAvatar && !imageErrors[avatarErrorKey] ? (
                                <img
                                  src={senderAvatar}
                                  height={36}
                                  width={36}
                                  alt={senderName}
                                  className="rounded-full sm:h-8 sm:w-8 h-6 w-6 object-cover"
                                  onError={() => {
                                    console.warn('🖼️ Avatar image failed to load for:', msg.senderId, senderAvatar);
                                    setImageErrors((prev) => ({ ...prev, [avatarErrorKey]: true }));
                                  }}
                                />
                              ) : (
                                <div className="rounded-full sm:h-8 sm:w-8 h-6 w-6 bg-[#DAF9F9] flex items-center justify-center">
                                  <span className="sm:text-sm text-xs font-semibold text-[#00A9A9]">
                                    {getUserInitials(senderName)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-xs text-ld opacity-60 font-medium mb-2">{senderName}, {formattedTime}</div>
                              {msg.type === "text" && (
                                <div className="inline-block p-3 bg-[#F1FBFB] text-[#121D2E] font-inter rounded-lg max-w-[70%] whitespace-pre-wrap break-words">
                                  <Linkify options={{ target: '_blank', rel: 'noopener noreferrer', className: 'text-blue-600 underline hover:text-blue-800 break-all' }}>{msg.msg}</Linkify>
                                </div>
                              )}
                              {msg.type === "image" && (
                                <img src={msg.msg} height={200} width={200} alt="shared" className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity" onClick={() => handleMediaClick(msg.msg, 'image')} />
                              )}
                              {msg.attachment?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {msg.attachment.map((file: any, idx: number) => (
                                    <div key={idx} onClick={() => handleMediaClick(file.url, 'file', file.file)} className="flex items-center gap-2 bg-gray-100 rounded px-3 py-2 hover:bg-gray-200 transition-colors cursor-pointer">
                                      <Icon icon="solar:document-outline" height={18} />
                                      <span className="text-sm">{file.file}</span>
                                      <span className="text-xs text-gray-500">({file.fileSize})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <div className="max-w-[70%]">
                              {msg.createdAt && <div className="text-xs text-ld opacity-60 font-medium mb-2 text-end">{formattedTime}</div>}
                              {msg.type === "text" && (
                                <div className="inline-block p-3 bg-[#DAF9F9] text-[#121D2E] font-inter rounded-lg whitespace-pre-wrap break-words">
                                  <Linkify options={{ target: '_blank', rel: 'noopener noreferrer', className: 'text-blue-700 underline hover:text-blue-900 break-all' }}>{msg.msg}</Linkify>
                                </div>
                              )}
                              {msg.type === "image" && (
                                <img src={msg.msg} height={200} width={200} alt="shared" className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity" onClick={() => handleMediaClick(msg.msg, 'image')} />
                              )}
                              {msg.attachment?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2 justify-end">
                                  {msg.attachment.map((file: any, idx: number) => (
                                    <div key={idx} onClick={() => handleMediaClick(file.url, 'file', file.file)} className="flex items-center gap-2 bg-gray-100 rounded px-3 py-2 hover:bg-gray-200 transition-colors cursor-pointer">
                                      <Icon icon="solar:document-outline" height={18} />
                                      <span className="text-sm">{file.file}</span>
                                      <span className="text-xs text-gray-500">({file.fileSize})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center h-full py-20">
                    <div className="text-center">
                      <Icon icon="solar:chat-line-linear" height={60} className="mx-auto mb-3 text-gray-400" />
                      <p className="text-sm text-gray-500">No messages yet</p>
                      <p className="text-xs text-gray-400 mt-1">Start the conversation!</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </SimpleBar>
        </div>

        {isRightSide && (
          <div className="xl:w-[260px] xl:block hidden overflow-y-auto overflow-x-hidden">
            <ChatInsideSidebar />
          </div>
        )}
      </div>

      <ChatMsgSent />

      {/* Mobile sidebar drawer */}
      {isOpenMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 xl:hidden" onClick={handleClose}>
          <div className="absolute right-0 top-0 bottom-0 w-[300px] bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4">
              <Button color="light" size="sm" onClick={handleClose} className="mb-4">Close</Button>
            </div>
            <ChatInsideSidebar />
          </div>
        </div>
      )}

      <MediaViewer
        isOpen={mediaViewer.isOpen}
        onClose={() => setMediaViewer({ ...mediaViewer, isOpen: false })}
        mediaUrl={mediaViewer.url}
        mediaType={mediaViewer.type}
        fileName={mediaViewer.fileName}
      />
    </div>
  );
};

export default ChatContent;